import { useQuery } from "@tanstack/react-query";
import { useBranding } from "@/hooks/use-branding";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Bot, Globe, FileText, MessageSquare, Layers, Sparkles,
  ArrowRight, Send, X, ChevronDown, Shield, Zap, Clock,
  Users, BarChart3, Headphones, Star, Check, ExternalLink,
  Smartphone, Mail, Code2, Minimize2, Maximize2,
  Lock, KeyRound, Database, Plug, Building2, GraduationCap,
  Briefcase, LifeBuoy, UserCheck, Eye, FileCheck,
} from "lucide-react";
import { useParams, Link } from "wouter";
import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLogin, MasterLoginRequiredError } from "@/lib/auth";
import type { LandingPageConfig } from "./settings-page";

const DEFAULT_CONFIG: LandingPageConfig = {
  heroTitle: "",
  heroSubtitle: "",
  heroBadge: "",
  headerStyle: "default",
  bannerGradientFrom: "",
  bannerGradientTo: "",
  bannerOverlayOpacity: 30,
  fontFamily: "Inter",
  headingSize: "large",
  bodySize: "medium",
  showFeatures: true,
  featuresTitle: "Intelligent Assistance at Your Fingertips",
  featuresSubtitle: "Our AI assistants are trained on our knowledge base to provide accurate, relevant answers to your questions.",
  showHowItWorks: true,
  showStats: true,
  showChannels: true,
  channelsTitle: "Reach Your Audience Anywhere",
  channelsSubtitle: "Deploy the same intelligent assistant across web, mobile, SMS, email, and any system that speaks REST.",
  showIntegrations: true,
  integrationsTitle: "Plugs Into Your Stack",
  integrationsSubtitle: "Pre-built connectors hand the AI the tools it needs — HRMS, ITSM, SIS, LMS, identity, and more.",
  showLiveAgent: true,
  liveAgentTitle: "Human Backup, One Click Away",
  liveAgentSubtitle: "When an answer needs a person, visitors are routed FIFO to a live operator with the full chat transcript pre-loaded.",
  showTrust: true,
  trustTitle: "Built for Enterprise & Higher-Ed Compliance",
  showCta: true,
  ctaTitle: "Ready to Get Started?",
  ctaSubtitle: "Choose one of our AI assistants above and start getting instant, intelligent answers to your questions.",
  footerText: "",
};

const BOT_TYPE_META: Record<string, { icon: any; label: string; accent: string }> = {
  website: { icon: Globe, label: "Website Bot", accent: "bg-sky-500/20 text-sky-400" },
  document: { icon: FileText, label: "Document Bot", accent: "bg-emerald-500/20 text-emerald-400" },
  combo: { icon: Layers, label: "Combo Bot", accent: "bg-violet-500/20 text-violet-400" },
  "ai-enhanced": { icon: Sparkles, label: "AI-Enhanced", accent: "bg-amber-500/20 text-amber-400" },
};

function hexToHSL(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function brandShades(hex: string) {
  const { h, s } = hexToHSL(hex);
  return {
    50: `hsl(${h}, ${Math.min(s + 20, 100)}%, 96%)`,
    100: `hsl(${h}, ${Math.min(s + 15, 100)}%, 90%)`,
    200: `hsl(${h}, ${Math.min(s + 10, 100)}%, 80%)`,
    500: `hsl(${h}, ${s}%, 45%)`,
    600: `hsl(${h}, ${s}%, 38%)`,
    700: `hsl(${h}, ${s}%, 30%)`,
    800: `hsl(${h}, ${s}%, 20%)`,
    900: `hsl(${h}, ${s}%, 12%)`,
    950: `hsl(${h}, ${s}%, 7%)`,
  };
}

function getHeadingSizeClasses(size: string) {
  switch (size) {
    case "small": return "text-2xl md:text-3xl lg:text-4xl";
    case "medium": return "text-3xl md:text-4xl lg:text-5xl";
    default: return "text-4xl md:text-5xl lg:text-6xl";
  }
}

function getSectionHeadingSizeClasses(size: string) {
  switch (size) {
    case "small": return "text-xl md:text-2xl";
    case "medium": return "text-2xl md:text-3xl";
    default: return "text-3xl md:text-4xl";
  }
}

function getBodySizeClasses(size: string) {
  switch (size) {
    case "small": return "text-sm md:text-base";
    case "large": return "text-xl md:text-2xl";
    default: return "text-lg md:text-xl";
  }
}

function getBodySmallClasses(size: string) {
  switch (size) {
    case "small": return "text-xs";
    case "large": return "text-base";
    default: return "text-sm";
  }
}

function ChatWidget({ bot, baseUrl, onClose }: { bot: any; baseUrl: string; onClose: () => void }) {
  const branding = useBranding();
  const [messages, setMessages] = useState<{ role: string; content: string }[]>([
    { role: "assistant", content: bot.welcomeMessage || "Hello! How can I help you?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sessionId = useRef(`landing-${bot.id}-${Date.now()}`);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/api/v1/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${bot.apiKey}` },
        body: JSON.stringify({ message: msg, session_id: sessionId.current }),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      if (!data.reply) throw new Error("No reply");
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    }
    setLoading(false);
  };

  const brandColor = bot.primaryColor || "#0c3547";

  if (minimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setMinimized(false)}
          className="w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white transition-transform hover:scale-110"
          style={{ backgroundColor: brandColor }}
          data-testid="button-chat-expand"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white dark:bg-gray-900 flex flex-col" style={{ height: "520px" }} data-testid="chat-widget-panel">
      <div className="px-4 py-3 flex items-center gap-3 text-white" style={{ backgroundColor: brandColor }}>
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
          <Bot className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{bot.name}</p>
          <p className="text-[11px] text-white/70">Online now</p>
        </div>
        <button onClick={() => setMinimized(true)} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" data-testid="button-chat-minimize">
          <Minimize2 className="w-4 h-4" />
        </button>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" data-testid="button-chat-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-950">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role !== "user" && (
              <div className="w-7 h-7 rounded-full flex items-center justify-center mr-2 mt-1 flex-shrink-0" style={{ backgroundColor: `${brandColor}20` }}>
                <Bot className="w-3.5 h-3.5" style={{ color: brandColor }} />
              </div>
            )}
            <div
              className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                m.role === "user"
                  ? "text-white rounded-br-md"
                  : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-bl-md shadow-sm border border-gray-100 dark:border-gray-700"
              }`}
              style={m.role === "user" ? { backgroundColor: brandColor } : undefined}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="w-7 h-7 rounded-full flex items-center justify-center mr-2 flex-shrink-0" style={{ backgroundColor: `${brandColor}20` }}>
              <Bot className="w-3.5 h-3.5" style={{ color: brandColor }} />
            </div>
            <div className="bg-white dark:bg-gray-800 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: "150ms" }} />
                <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-2">
          <input
            className="flex-1 text-sm px-4 py-2.5 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ "--tw-ring-color": brandColor } as any}
            placeholder="Type a message..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && sendMessage()}
            data-testid="input-landing-chat"
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-all disabled:opacity-40 hover:opacity-90"
            style={{ backgroundColor: brandColor }}
            data-testid="button-landing-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-center text-[10px] text-gray-400 mt-2">
          Powered by <span className="font-semibold">{branding.shortName}</span> — {branding.poweredBy}
        </p>
      </div>
    </div>
  );
}

function BotCard({ bot, brand, onChat }: { bot: any; brand: string; onChat: () => void }) {
  const meta = BOT_TYPE_META[bot.type] || BOT_TYPE_META.website;
  const Icon = meta.icon;
  const channels = bot.enabledChannels || ["web"];

  return (
    <Card className="group relative overflow-hidden border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-800/50 hover:shadow-xl hover:shadow-black/5 dark:hover:shadow-black/20 transition-all duration-300 hover:-translate-y-1" data-testid={`card-bot-${bot.id}`}>
      <div className="absolute top-0 left-0 right-0 h-1 transition-all duration-300 group-hover:h-1.5" style={{ backgroundColor: bot.primaryColor || brand }} />
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg" style={{ backgroundColor: bot.primaryColor || brand }}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-base">{bot.name}</h3>
              <Badge variant="secondary" className={`text-[10px] mt-1 ${meta.accent}`}>{meta.label}</Badge>
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4 line-clamp-2">
          {bot.scopeDescription || bot.welcomeMessage || "AI-powered assistant ready to help you."}
        </p>

        <div className="flex items-center gap-1.5 mb-5">
          {channels.includes("web") && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300">
              <Globe className="w-3 h-3" /> Web
            </span>
          )}
          {channels.includes("sms") && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300">
              <Smartphone className="w-3 h-3" /> SMS
            </span>
          )}
          {channels.includes("email") && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300">
              <Mail className="w-3 h-3" /> Email
            </span>
          )}
          {channels.includes("api") && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300">
              <Code2 className="w-3 h-3" /> API
            </span>
          )}
        </div>

        <Button
          className="w-full text-white shadow-md hover:shadow-lg transition-all"
          style={{ backgroundColor: bot.primaryColor || brand }}
          onClick={onChat}
          data-testid={`button-chat-${bot.id}`}
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Start Conversation
          <ArrowRight className="w-4 h-4 ml-auto" />
        </Button>
      </CardContent>
    </Card>
  );
}

function SignInDialog({
  open,
  onOpenChange,
  brand,
  orgName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  brand: string;
  orgName: string;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const login = useLogin();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrMsg(null);
    try {
      await login.mutateAsync({ email, password });
      // Hard-redirect so AuthenticatedApp re-evaluates and the staff
      // dashboard mounts cleanly.
      window.location.href = "/";
    } catch (err: any) {
      // Master admins must use the dedicated two-step flow — redirect
      // instead of surfacing a dead-end error on this org surface.
      if (err instanceof MasterLoginRequiredError) {
        window.location.href = err.redirect || "/master/login";
        return;
      }
      // apiRequest throws "<status>: <body>" where <body> is usually a
      // JSON string like {"message":"Invalid email or password"}.
      const raw = (err?.message || "Sign in failed").replace(/^\d+:\s*/, "");
      let friendly = raw;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.message === "string") friendly = parsed.message;
      } catch {
        // not JSON — keep raw text as-is
      }
      setErrMsg(friendly || "Sign in failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-signin">
        <DialogHeader>
          <DialogTitle>Sign in to {orgName}</DialogTitle>
          <DialogDescription>
            Staff and admins — use your work email and password to access the dashboard.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="signin-email">Email</Label>
            <Input
              id="signin-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="input-signin-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="signin-password">Password</Label>
            <Input
              id="signin-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="input-signin-password"
            />
          </div>
          {errMsg && (
            <div
              className="text-sm rounded-md border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 text-red-700 dark:text-red-300 px-3 py-2"
              data-testid="text-signin-error"
            >
              {errMsg}
            </div>
          )}
          <Button
            type="submit"
            disabled={login.isPending}
            className="w-full text-white shadow-md"
            style={{ backgroundColor: brand }}
            data-testid="button-signin-submit"
          >
            {login.isPending ? "Signing in..." : "Sign In"}
          </Button>
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            Need a full-screen login or SSO? <Link href="/login" className="underline hover:text-gray-700 dark:hover:text-gray-200" data-testid="link-full-login">Open the staff login page</Link>.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function OrgLanding() {
  const branding = useBranding();
  const { slug } = useParams<{ slug: string }>();
  const [signInOpen, setSignInOpen] = useState(false);
  const { data, isLoading, error } = useQuery<any>({
    queryKey: ["/api/public/org", slug],
    queryFn: async () => {
      const res = await fetch(`/api/public/org/${slug}`);
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
  });
  const [activeBot, setActiveBot] = useState<any>(null);
  const botsRef = useRef<HTMLDivElement>(null);
  const baseUrl = window.location.origin;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-sky-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Organization Not Found</h1>
          <p className="text-gray-500 text-sm">The organization you're looking for doesn't exist or may have been removed.</p>
        </div>
      </div>
    );
  }

  const { organization, chatbots } = data;
  const brand = organization.brandColor || "#0c3547";
  const shades = brandShades(brand);
  const cfg: LandingPageConfig = { ...DEFAULT_CONFIG, ...(organization.landingPageConfig || {}) };

  const fontFamily = `'${cfg.fontFamily}', sans-serif`;
  const heroTitle = cfg.heroTitle || `Welcome to ${organization.name}`;
  const heroSubtitle = cfg.heroSubtitle || "Explore our AI-powered assistants, designed to help you find answers, get support, and accomplish more — instantly.";
  const gradFrom = cfg.bannerGradientFrom || shades[50];
  const gradTo = cfg.bannerGradientTo || "white";
  const overlayOpacity = cfg.bannerOverlayOpacity / 100;

  const scrollToBots = () => {
    botsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fontLink = cfg.fontFamily !== "Inter"
    ? `https://fonts.googleapis.com/css2?family=${cfg.fontFamily.replace(/\s+/g, "+")}:wght@400;500;600;700&display=swap`
    : null;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 overflow-x-hidden" style={{ fontFamily }}>
      {fontLink && <link rel="stylesheet" href={fontLink} />}

      <nav className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl border-b border-gray-200/50 dark:border-white/5" style={{ backgroundColor: "rgba(255,255,255,0.85)" }}>
        <div className={`max-w-7xl mx-auto px-6 h-16 flex items-center ${cfg.headerStyle === "centered" ? "justify-center" : "justify-between"}`}>
          <div className={`flex items-center gap-3 ${cfg.headerStyle === "centered" ? "absolute left-6" : ""}`}>
            {organization.logo ? (
              <img src={organization.logo} alt={organization.name} className="h-9 w-auto object-contain" data-testid="img-org-logo" />
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: brand }} data-testid="icon-org-fallback">
                {organization.name.charAt(0).toUpperCase()}
              </div>
            )}
            {cfg.headerStyle !== "minimal" && (
              <span className="font-bold text-gray-900 dark:text-white text-lg" data-testid="text-nav-org-name">
                {organization.name}
              </span>
            )}
          </div>
          {cfg.headerStyle === "centered" && (
            <span className="font-bold text-gray-900 dark:text-white text-xl" data-testid="text-nav-org-name-center">
              {organization.name}
            </span>
          )}
          <div className={`flex items-center gap-3 ${cfg.headerStyle === "centered" ? "absolute right-6" : ""}`}>
            <Button
              variant="ghost"
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm hidden sm:inline-flex"
              onClick={scrollToBots}
              data-testid="nav-our-bots"
            >
              Our Assistants
            </Button>
            <Button
              variant="ghost"
              className="text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white text-sm"
              onClick={() => setSignInOpen(true)}
              data-testid="nav-sign-in"
            >
              Sign In
            </Button>
            <Button
              className="text-white text-sm shadow-md"
              style={{ backgroundColor: brand }}
              onClick={scrollToBots}
              data-testid="nav-get-started"
            >
              Get Started <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      <section className="relative pt-28 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${gradFrom} 0%, ${gradTo} 50%, ${gradFrom} 100%)` }} />
        <div className="absolute top-20 -left-20 w-72 h-72 rounded-full blur-3xl" style={{ backgroundColor: shades[200], opacity: overlayOpacity }} />
        <div className="absolute bottom-10 -right-20 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: shades[200], opacity: overlayOpacity * 0.67 }} />

        <div className="relative max-w-5xl mx-auto text-center">
          {organization.logo ? (
            <img
              src={organization.logo}
              alt={organization.name}
              className="h-20 w-auto mx-auto mb-8 object-contain"
              data-testid="img-hero-logo"
            />
          ) : (
            <div
              className="w-20 h-20 rounded-2xl mx-auto mb-8 flex items-center justify-center text-white text-3xl font-bold shadow-xl"
              style={{ backgroundColor: brand }}
              data-testid="icon-hero-fallback"
            >
              {organization.name.charAt(0).toUpperCase()}
            </div>
          )}

          {cfg.heroBadge && (
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-xs font-semibold border" style={{ backgroundColor: `${brand}12`, color: brand, borderColor: `${brand}40` }} data-testid="badge-hero">
              <Sparkles className="w-3.5 h-3.5" />
              {cfg.heroBadge}
            </div>
          )}

          <h1 className={`${getHeadingSizeClasses(cfg.headingSize)} font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1] mb-6`} data-testid="text-hero-title">
            {heroTitle.includes(organization.name) ? (
              <>
                {heroTitle.split(organization.name)[0]}
                <span style={{ color: brand }}>{organization.name}</span>
                {heroTitle.split(organization.name).slice(1).join(organization.name)}
              </>
            ) : (
              heroTitle
            )}
          </h1>

          <p className={`${getBodySizeClasses(cfg.bodySize)} text-gray-500 dark:text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed`} data-testid="text-hero-subtitle">
            {heroSubtitle}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button
              size="lg"
              className="text-white px-8 text-base shadow-lg hover:shadow-xl transition-all"
              style={{ backgroundColor: brand }}
              onClick={scrollToBots}
              data-testid="hero-explore-bots"
            >
              Explore Assistants <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {cfg.showStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              <div className="text-center p-4 rounded-xl bg-white/80 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 shadow-sm" data-testid="stat-bots">
                <div className="text-2xl font-bold" style={{ color: brand }}>{chatbots.length}</div>
                <div className="text-gray-500 text-xs mt-1">AI Assistants</div>
              </div>
              <div className="text-center p-4 rounded-xl bg-white/80 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 shadow-sm" data-testid="stat-available">
                <div className="text-2xl font-bold" style={{ color: brand }}>24/7</div>
                <div className="text-gray-500 text-xs mt-1">Availability</div>
              </div>
              <div className="text-center p-4 rounded-xl bg-white/80 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 shadow-sm" data-testid="stat-instant">
                <div className="text-2xl font-bold" style={{ color: brand }}>&lt;1s</div>
                <div className="text-gray-500 text-xs mt-1">Response Time</div>
              </div>
              <div className="text-center p-4 rounded-xl bg-white/80 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 shadow-sm" data-testid="stat-channels">
                <div className="text-2xl font-bold" style={{ color: brand }}>Multi</div>
                <div className="text-gray-500 text-xs mt-1">Channel Support</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {cfg.showFeatures && (
        <section className="py-20 px-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="text-xs mb-4 border" style={{ backgroundColor: `${brand}15`, color: brand, borderColor: `${brand}30` }}>
                Why Choose Us
              </Badge>
              <h2 className={`${getSectionHeadingSizeClasses(cfg.headingSize)} font-bold text-gray-900 dark:text-white mb-4`}>
                {cfg.featuresTitle}
              </h2>
              <p className={`${getBodySmallClasses(cfg.bodySize)} text-gray-500 dark:text-gray-400 max-w-2xl mx-auto`}>
                {cfg.featuresSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Zap, title: "Instant Responses", desc: "Get answers in seconds, not hours. Our AI processes your questions and delivers precise information immediately." },
                { icon: Clock, title: "Available 24/7", desc: "No waiting for business hours. Our assistants are always online and ready to help whenever you need them." },
                { icon: Shield, title: "Secure & Private", desc: "Your conversations are protected with enterprise-grade security. We respect your privacy at every step." },
                { icon: Users, title: "Personalized Help", desc: "Each assistant is specialized in its domain, providing contextual and relevant answers tailored to your needs." },
                { icon: BarChart3, title: "Knowledge-Driven", desc: "Powered by curated knowledge sources — websites, documents, and expert content for accurate responses." },
                { icon: Headphones, title: "Seamless Support", desc: "From simple queries to complex questions, our AI assistants guide you through every step of the process." },
              ].map((item) => (
                <div key={item.title} className="p-6 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 shadow-sm hover:shadow-md transition-shadow" data-testid={`feature-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${brand}15` }}>
                    <item.icon className="w-5 h-5" style={{ color: brand }} />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                  <p className={`${getBodySmallClasses(cfg.bodySize)} text-gray-500 dark:text-gray-400 leading-relaxed`}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section ref={botsRef} className="py-20 px-6 border-t border-gray-100 dark:border-gray-800" id="assistants">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <Badge className="text-xs mb-4 border" style={{ backgroundColor: `${brand}15`, color: brand, borderColor: `${brand}30` }}>
              Our AI Assistants
            </Badge>
            <h2 className={`${getSectionHeadingSizeClasses(cfg.headingSize)} font-bold text-gray-900 dark:text-white mb-4`}>
              Choose an Assistant to Get Started
            </h2>
            <p className={`${getBodySmallClasses(cfg.bodySize)} text-gray-500 dark:text-gray-400 max-w-2xl mx-auto`}>
              Select the assistant that best fits your needs and start a conversation right away.
            </p>
          </div>

          {chatbots.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                <Bot className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">No Assistants Available Yet</h3>
              <p className="text-gray-500 text-sm">This organization is setting up their AI assistants. Check back soon!</p>
            </div>
          ) : (
            <div className={`grid gap-6 ${chatbots.length === 1 ? "max-w-md mx-auto" : chatbots.length === 2 ? "grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
              {chatbots.map((bot: any) => (
                <BotCard
                  key={bot.id}
                  bot={bot}
                  brand={brand}
                  onChat={() => setActiveBot(bot)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {cfg.showChannels && (
        <section className="py-20 px-6 bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800" data-testid="section-channels">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="text-xs mb-4 border" style={{ backgroundColor: `${brand}15`, color: brand, borderColor: `${brand}30` }}>
                Omnichannel Delivery
              </Badge>
              <h2 className={`${getSectionHeadingSizeClasses(cfg.headingSize)} font-bold text-gray-900 dark:text-white mb-4`}>
                {cfg.channelsTitle}
              </h2>
              <p className={`${getBodySmallClasses(cfg.bodySize)} text-gray-500 dark:text-gray-400 max-w-2xl mx-auto`}>
                {cfg.channelsSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { icon: Globe, title: "Web Widget", desc: "Drop-in script for any site." },
                { icon: Smartphone, title: "SMS / WhatsApp", desc: "Reach users on their phone." },
                { icon: Mail, title: "Email Inbox", desc: "Auto-replies to support email." },
                { icon: MessageSquare, title: "Mobile / In-App", desc: "Native iOS & Android SDKs." },
                { icon: Code2, title: "REST API", desc: "Embed in any backend." },
              ].map((c) => (
                <div key={c.title} className="p-5 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 hover:shadow-md transition-shadow text-center" data-testid={`channel-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${brand}15`, color: brand }}>
                    <c.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{c.title}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {cfg.showIntegrations && (
        <section className="py-20 px-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800" data-testid="section-integrations">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="text-xs mb-4 border" style={{ backgroundColor: `${brand}15`, color: brand, borderColor: `${brand}30` }}>
                Integrations
              </Badge>
              <h2 className={`${getSectionHeadingSizeClasses(cfg.headingSize)} font-bold text-gray-900 dark:text-white mb-4`}>
                {cfg.integrationsTitle}
              </h2>
              <p className={`${getBodySmallClasses(cfg.bodySize)} text-gray-500 dark:text-gray-400 max-w-2xl mx-auto`}>
                {cfg.integrationsSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { icon: GraduationCap, label: "Banner SIS", category: "Student Info" },
                { icon: Briefcase, label: "Workday", category: "HRMS" },
                { icon: LifeBuoy, label: "ServiceNow", category: "ITSM" },
                { icon: Building2, label: "Salesforce", category: "CRM" },
                { icon: FileText, label: "Canvas LMS", category: "Learning" },
                { icon: KeyRound, label: "Okta / Azure AD", category: "SSO / SAML" },
                { icon: Database, label: "SharePoint", category: "Documents" },
                { icon: Plug, label: "Custom Webhook", category: "Anything" },
              ].map((i) => (
                <div key={i.label} className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 flex items-center gap-3 hover:shadow-md transition-shadow" data-testid={`integration-${i.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${brand}15`, color: brand }}>
                    <i.icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{i.label}</p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{i.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {cfg.showLiveAgent && (
        <section className="py-20 px-6 bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800" data-testid="section-live-agent">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="text-xs mb-4 border" style={{ backgroundColor: `${brand}15`, color: brand, borderColor: `${brand}30` }}>
                  Live Agent Handoff
                </Badge>
                <h2 className={`${getSectionHeadingSizeClasses(cfg.headingSize)} font-bold text-gray-900 dark:text-white mb-4`}>
                  {cfg.liveAgentTitle}
                </h2>
                <p className={`${getBodySmallClasses(cfg.bodySize)} text-gray-500 dark:text-gray-400 mb-6 leading-relaxed`}>
                  {cfg.liveAgentSubtitle}
                </p>
                <ul className="space-y-3">
                  {[
                    "FIFO queue with real-time wait-time display",
                    "Operators see the full AI transcript before joining",
                    "Visitor metadata: name, email, IP, device, country",
                    "File attachments up to 5 MB in either direction",
                    "Offline form when no operators are online",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: brand }} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="relative">
                <div className="absolute -inset-6 rounded-3xl blur-2xl opacity-20" style={{ backgroundColor: brand }} />
                <div className="relative rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 shadow-xl overflow-hidden">
                  <div className="flex items-center gap-3 p-4 border-b border-gray-100 dark:border-gray-700/50" style={{ backgroundColor: `${brand}08` }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: brand }}>
                      <UserCheck className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900 dark:text-white">Connected to Sarah</p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live agent — typically replies in 30s
                      </p>
                    </div>
                  </div>
                  <div className="p-5 space-y-3 bg-gray-50 dark:bg-gray-900/30">
                    <div className="flex justify-end">
                      <div className="max-w-[80%] px-3.5 py-2 rounded-2xl text-xs text-white rounded-br-md" style={{ backgroundColor: brand }}>
                        Hi! Can a human verify my financial-aid balance?
                      </div>
                    </div>
                    <div className="flex justify-start gap-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${brand}20`, color: brand }}>
                        <UserCheck className="w-3 h-3" />
                      </div>
                      <div className="bg-white dark:bg-gray-800 px-3.5 py-2 rounded-2xl rounded-bl-md text-xs text-gray-800 dark:text-gray-200 border border-gray-100 dark:border-gray-700">
                        Of course — I can see your transcript. Pulling your record now.
                      </div>
                    </div>
                    <div className="flex justify-start gap-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${brand}20`, color: brand }}>
                        <UserCheck className="w-3 h-3" />
                      </div>
                      <div className="bg-white dark:bg-gray-800 px-3.5 py-2 rounded-2xl rounded-bl-md text-xs text-gray-800 dark:text-gray-200 border border-gray-100 dark:border-gray-700">
                        Looks good — your aid posted yesterday. Anything else?
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {cfg.showTrust && (
        <section className="py-20 px-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800" data-testid="section-trust">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="text-xs mb-4 border" style={{ backgroundColor: `${brand}15`, color: brand, borderColor: `${brand}30` }}>
                Security & Compliance
              </Badge>
              <h2 className={`${getSectionHeadingSizeClasses(cfg.headingSize)} font-bold text-gray-900 dark:text-white mb-4`}>
                {cfg.trustTitle}
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { icon: Lock, label: "End-to-end TLS" },
                { icon: KeyRound, label: "SSO / SAML / OIDC" },
                { icon: Shield, label: "FERPA-aware" },
                { icon: Eye, label: "PII Redaction" },
                { icon: FileCheck, label: "Audit Logs" },
                { icon: Database, label: "Data Residency" },
              ].map((b) => (
                <div key={b.label} className="p-5 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 flex flex-col items-center text-center hover:shadow-md transition-shadow" data-testid={`trust-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${brand}15`, color: brand }}>
                    <b.icon className="w-5 h-5" />
                  </div>
                  <p className="font-semibold text-xs text-gray-900 dark:text-white">{b.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {cfg.showHowItWorks && (
        <section className="py-20 px-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="text-xs mb-4 border" style={{ backgroundColor: `${brand}15`, color: brand, borderColor: `${brand}30` }}>
                How It Works
              </Badge>
              <h2 className={`${getSectionHeadingSizeClasses(cfg.headingSize)} font-bold text-gray-900 dark:text-white mb-4`}>
                Getting Help Is Easy
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { step: "1", title: "Choose an Assistant", desc: "Browse our specialized AI assistants and select the one that matches your needs." },
                { step: "2", title: "Ask Your Question", desc: "Type your question in natural language — no special commands needed." },
                { step: "3", title: "Get Instant Answers", desc: "Receive accurate, detailed responses powered by our curated knowledge base." },
              ].map((item) => (
                <div key={item.step} className="text-center" data-testid={`step-${item.step}`}>
                  <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white text-xl font-bold shadow-lg" style={{ backgroundColor: brand }}>
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                  <p className={`${getBodySmallClasses(cfg.bodySize)} text-gray-500 dark:text-gray-400 leading-relaxed`}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {cfg.showCta && (
        <section className="py-16 px-6 border-t border-gray-100 dark:border-gray-800">
          <div className="max-w-4xl mx-auto text-center" style={{ background: `linear-gradient(135deg, ${shades[50]}, white, ${shades[50]})`, borderRadius: "1.5rem", padding: "3rem 2rem", border: `1px solid ${shades[100]}` }}>
            <h2 className={`${getSectionHeadingSizeClasses(cfg.headingSize)} font-bold text-gray-900 mb-4`} data-testid="text-cta-title">
              {cfg.ctaTitle}
            </h2>
            <p className={`${getBodySmallClasses(cfg.bodySize)} text-gray-500 max-w-lg mx-auto mb-8`}>
              {cfg.ctaSubtitle}
            </p>
            <Button
              size="lg"
              className="text-white px-10 text-base shadow-lg"
              style={{ backgroundColor: brand }}
              onClick={scrollToBots}
              data-testid="cta-explore-bots"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Start a Conversation
            </Button>
          </div>
        </section>
      )}

      <footer className="border-t border-gray-200 dark:border-gray-800 py-10 px-6 bg-gray-50 dark:bg-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              {organization.logo ? (
                <img src={organization.logo} alt={organization.name} className="h-8 w-auto object-contain opacity-70" />
              ) : (
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: brand }}>
                  {organization.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="font-semibold text-gray-700 dark:text-gray-300 text-sm">{organization.name}</span>
            </div>

            <div className="flex items-center gap-6 text-xs text-gray-400">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" /> Secure
                </span>
                <span className="flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5" /> AI-Powered
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> 24/7
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-xs">
              {cfg.footerText || `\u00A9 ${new Date().getFullYear()} ${organization.name}. All rights reserved.`}
            </p>
            <p className="text-gray-400 text-xs flex items-center gap-1.5">
              Powered by <Bot className="w-3.5 h-3.5" /> <span className="font-semibold">{branding.shortName}</span> — {branding.poweredBy}
            </p>
          </div>
        </div>
      </footer>

      {activeBot && (
        <ChatWidget
          key={activeBot.id}
          bot={activeBot}
          baseUrl={baseUrl}
          onClose={() => setActiveBot(null)}
        />
      )}

      <SignInDialog
        open={signInOpen}
        onOpenChange={setSignInOpen}
        brand={brand}
        orgName={organization.name}
      />
    </div>
  );
}
